'use client';

import { useQuery } from '@tanstack/react-query';
import { useCellServiceSession } from '@/hooks/useCellServiceSession';
import { resolveMobileAcquisitionIds } from '@/shared/mobile-service/api/cors-mobile-api';
import { useAcquisitionMap } from '@/shared/mobile-service/hooks/useAcquisitions';
import { ufdrSearchableScope } from '@/shared/ufdr-mobile-adapter';

/**
 * Pin a UFDR preview to one mobile-service acquisition.
 *
 * An explicit id (citation or `/mobile-viewer?acquisitionId=`) wins. Otherwise
 * this uses the same case-folder plus searchable-path match as the assistant.
 * An `s3://` URL is not a searchable path and is ignored.
 */
export function useMobileForensicAcquisition(args: {
  enabled: boolean;
  path?: string | null;
  caseFolderName?: string | null;
  acquisitionId?: number | null;
}) {
  const session = useCellServiceSession();
  const searchable = ufdrSearchableScope(args.path) ?? '';
  const canResolve =
    args.enabled &&
    session.enabled &&
    args.acquisitionId == null &&
    Boolean(searchable) &&
    Boolean(args.caseFolderName);

  const resolution = useQuery({
    queryKey: ['ufdr-acquisition', session.cellId, args.caseFolderName ?? '', searchable],
    enabled: canResolve,
    staleTime: 5 * 60 * 1000,
    queryFn: ({ signal }) =>
      resolveMobileAcquisitionIds({
        cellId: session.cellId as string,
        accessToken: session.accessToken as string,
        caseFolderName: args.caseFolderName,
        scopedPaths: [searchable],
        signal,
      }),
  });

  const acquisitionId =
    args.acquisitionId ??
    (resolution.data?.status === 'unique' ? resolution.data.id : null);
  const mapRow = useAcquisitionMap(args.enabled && acquisitionId != null ? acquisitionId : null);

  return {
    acquisitionId,
    caseName: mapRow.data?.caseName ?? '',
    loading: (canResolve && resolution.isLoading) || (acquisitionId != null && mapRow.isLoading),
    error: resolution.error?.message ?? mapRow.error?.message ?? null,
  };
}
